import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DxButtonModule, DxLoadIndicatorModule, DxNumberBoxModule, DxPopupModule, DxProgressBarModule, DxSelectBoxModule,
  DxTextBoxModule
} from 'devextreme-angular';
import { firstValueFrom } from 'rxjs';
import { CataloguePlanningService } from '../services/catalogue-planning.service';
import { ConfirmationService } from '../services/confirmation.service';
import { ProductSelectionComponent } from '../product-selection/product-selection.component';
import {
  CATALOGUE_LIMITS, CataloguePage, CataloguePageDto, CataloguePlacement, CataloguePlan, CatalogueProduct,
  CatalogueProductFeed, CatalogueSaveRequest
} from '../models/catalogue-planner.model';

/** A placeable feed product as a product-selection grid row; `id` is the productId:promotionGroupId key. */
export interface ProductSelectionRow {
  id: string;
  product_code: string;
  product_name: string;
  supplier_name: string;
  product_group_name: string;
  category_name: string;
  department_description: string;
  category_description: string;
  host_product_group_description: string;
  product_range: string;
}

/** The slice of a DevExtreme editor the planner needs in order to put a rejected value back. */
export interface ResettableEditor { option(name: string, value: unknown): void; }

@Component({
  selector: 'app-catalogue-planner',
  standalone: true,
  imports: [
    CommonModule, DxButtonModule, DxTextBoxModule, DxNumberBoxModule, DxSelectBoxModule, DxPopupModule,
    DxProgressBarModule, DxLoadIndicatorModule, ProductSelectionComponent
  ],
  templateUrl: './catalogue-planner.component.html',
  styleUrl: './catalogue-planner.component.css'
})
export class CataloguePlannerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ProductSelectionComponent) productSelection?: ProductSelectionComponent;
  readonly limits = CATALOGUE_LIMITS;
  promotionId = 0;
  promotion: { promotion_name?: string; name?: string } = {};
  pages: CataloguePage[] = [];
  products: CatalogueProduct[] = [];
  activePageId: number | string | null = null;
  productSelectionVisible = false;
  pageSearchQuery = '';
  jumpPageNumber = 1;
  loading = true;
  saving = false;
  loadError = '';
  saveError = '';
  /** Per-field messages mapped from the API's 400/422 validation errors. */
  validationMessages: string[] = [];
  capacityError = '';
  limitError = '';
  conflict = false;
  editingLocked = false;
  lockedReason = '';
  notice = '';
  version = 0;
  dirty = false;

  // Derived state. Recalculated by refresh() after every change, not on each change-detection pass.
  activePage: CataloguePage | undefined;
  activePageIndex = -1;
  filteredPages: CataloguePage[] = [];
  pageNumbers = new Map<number | string, number>();
  pageOptions: Array<{ index: number; text: string }> = [];
  assignedIds = new Set<number>();
  placedProducts: CatalogueProduct[] = [];
  /** Feed products not yet placed anywhere in this promotion. */
  availableProducts: CatalogueProduct[] = [];
  openSlots = 0;

  private beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    if (this.dirty) { event.preventDefault(); event.returnValue = ''; }
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly catalogue: CataloguePlanningService,
    private readonly confirmation: ConfirmationService,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  async ngOnInit(): Promise<void> {
    this.promotionId = Number(this.route.snapshot.paramMap.get('promotionHeaderId'));
    // Only trust the stored name when it belongs to this route (not after a deep link).
    const stored = JSON.parse(localStorage.getItem('selectedPromo') || '{}');
    this.promotion = Number(stored?.id) === this.promotionId ? stored : {};
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    await this.load();
  }

  private viewportRoot: HTMLElement | null = null;
  private viewportObserver?: ResizeObserver;
  private viewportFrame: number | null = null;
  private readonly scheduleViewportFit = (): void => {
    if (!this.viewportRoot || this.viewportFrame !== null) return;
    this.viewportFrame = requestAnimationFrame(() => {
      this.viewportFrame = null;
      if (!this.viewportRoot) return;
      // Banners above the app can wrap on small screens. Use the actual offset,
      // not a fixed toolbar-height subtraction, and retain space for the banner.
      const available = Math.max(0, window.innerHeight - this.viewportRoot.getBoundingClientRect().top);
      this.viewportRoot.style.setProperty('--catalogue-app-available-height', `${available}px`);
    });
  };

  ngAfterViewInit(): void {
    this.viewportRoot = this.elementRef.nativeElement.closest('app-root');
    if (!this.viewportRoot) return;
    window.addEventListener('resize', this.scheduleViewportFit);
    this.viewportObserver = new ResizeObserver(this.scheduleViewportFit);
    this.viewportObserver.observe(document.body);
    this.scheduleViewportFit();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    window.removeEventListener('resize', this.scheduleViewportFit);
    this.viewportObserver?.disconnect();
    if (this.viewportFrame !== null) cancelAnimationFrame(this.viewportFrame);
    this.viewportFrame = null;
    this.viewportRoot?.style.removeProperty('--catalogue-app-available-height');
    this.viewportRoot = null;
  }

  async load(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    try {
      const [plan, feed] = await Promise.all([
        firstValueFrom(this.catalogue.getPlan(this.promotionId)),
        firstValueFrom(this.catalogue.getProducts(this.promotionId))
      ]);
      this.version = Number(plan?.version || 0);
      this.applyEditability(plan);
      this.pages = this.toPages(plan);
      this.products = this.toProducts(feed);
      this.activePageId = this.pages[0]?.id ?? null;
      this.jumpPageNumber = this.pages.length ? 1 : 0;
      this.pageSearchQuery = '';
      this.dirty = false;
      this.conflict = false;
      this.saveError = '';
      this.validationMessages = [];
      this.limitError = '';
    } catch (error: any) {
      this.loadError = this.errorMessage(error, 'Catalogue planning could not be loaded.');
    } finally {
      this.loading = false;
      this.refresh();
    }
  }

  /** Recomputes every derived field. Call after any change to pages, products or the active page. */
  refresh(): void {
    this.pageNumbers = new Map(this.pages.map((page, index) => [page.id, index + 1]));
    this.activePage = this.pages.find(page => page.id === this.activePageId);
    this.activePageIndex = this.activePage ? this.pages.indexOf(this.activePage) : -1;
    this.assignedIds = new Set(this.pages.flatMap(page => page.products.map(product => product.productId)));
    this.pageOptions = this.pages.map((page, index) => ({
      index: index + 1,
      text: `${(index + 1).toString().padStart(2, '0')} — ${page.name || 'Untitled page'}`
    }));

    const rawQuery = this.pageSearchQuery.trim().toLocaleLowerCase();
    const numericSearch = rawQuery.startsWith('#');
    const query = rawQuery.replace(/^#/, '');
    this.filteredPages = !query ? this.pages : this.pages.filter((page, index) => numericSearch
      ? String(index + 1) === String(Number(query))
      : page.name.toLocaleLowerCase().includes(query) || String(index + 1).includes(query));

    const page = this.activePage;
    const feedByKey = new Map(this.products.map(product => [`${product.productId}:${product.promotionGroupId}`, product]));
    this.placedProducts = !page ? [] : page.products.map(placement => feedByKey.get(`${placement.productId}:${placement.promotionGroupId}`) ?? {
      productId: placement.productId,
      promotionGroupId: placement.promotionGroupId,
      code: placement.productCode || '',
      name: placement.productName || 'Saved product',
      planningCategoryId: page.planningCategoryId ?? 0,
      category: placement.planningCategoryName || page.category,
      // Saved, but missing from the eligible-product feed: the API will reject it on save.
      ineligible: true
    });
    this.openSlots = page ? Math.max(0, page.capacity - page.products.length) : 0;

    this.availableProducts = this.products.filter(product =>
      product.planningCategoryId > 0 && !this.assignedIds.has(product.productId));
  }

  trackPage = (_: number, page: CataloguePage) => page.id;
  trackPlacement = (_: number, product: CatalogueProduct) => `${product.productId}:${product.promotionGroupId}`;

  get canAddProducts(): boolean {
    return !this.editingLocked && !this.saving && this.openSlots > 0 && this.availableProducts.length > 0;
  }

  get totalPlacements(): number { return this.assignedIds.size; }

  selectPage(id: number | string): void {
    if (this.saving) return;
    this.activePageId = id;
    const pageIndex = this.pages.findIndex(page => page.id === id);
    if (pageIndex >= 0) this.jumpPageNumber = pageIndex + 1;
    this.capacityError = '';
    this.refresh();
    this.revealActivePage();
  }

  updatePageSearch(query: string): void {
    this.pageSearchQuery = query ?? '';
    this.refresh();
  }

  clearPageSearch(): void { this.updatePageSearch(''); }

  goToPage(pageNumber: number): void {
    this.jumpPageNumber = pageNumber;
    this.jumpToPage();
  }

  jumpToPage(): void {
    if (this.saving || !this.pages.length) return;
    const requested = Number(this.jumpPageNumber);
    if (!Number.isFinite(requested)) return;
    const index = Math.max(1, Math.min(this.pages.length, Math.trunc(requested))) - 1;
    this.pageSearchQuery = '';
    this.selectPage(this.pages[index].id);
  }

  previousPage(): void { this.stepPage(-1); }
  nextPage(): void { this.stepPage(1); }

  private stepPage(offset: number): void {
    if (this.saving) return;
    const index = this.activePageIndex;
    const target = index + offset;
    if (index >= 0 && target >= 0 && target < this.pages.length) {
      this.pageSearchQuery = '';
      this.selectPage(this.pages[target].id);
    }
  }

  private revealActivePage(): void {
    const pageId = this.activePageId;
    if (pageId == null) return;
    setTimeout(() => {
      if (this.activePageId !== pageId) return;
      const list = this.elementRef.nativeElement.querySelector<HTMLElement>('.page-list');
      const card = Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLElement>('.page-card'))
        .find(item => item.id === `planner-page-${String(pageId)}`);
      if (list && card) {
        const listBounds = list.getBoundingClientRect();
        const cardBounds = card.getBoundingClientRect();
        if (cardBounds.top < listBounds.top) list.scrollTop -= listBounds.top - cardBounds.top;
        else if (cardBounds.bottom > listBounds.bottom) list.scrollTop += cardBounds.bottom - listBounds.bottom;
      }
    });
  }

  addPage(): void {
    if (this.editingLocked || this.saving) return;
    if (this.pages.length >= CATALOGUE_LIMITS.maxPages) {
      this.limitError = `A catalogue can have at most ${CATALOGUE_LIMITS.maxPages} pages.`;
      return;
    }
    const page: CataloguePage = {
      id: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      name: '',
      planningCategoryId: null,
      category: '',
      capacity: 4,
      products: []
    };
    this.pages = [...this.pages, page];
    this.pageSearchQuery = '';
    this.limitError = '';
    this.selectPage(page.id);
    this.markDirty();
  }

  async removePage(page: CataloguePage): Promise<void> {
    if (this.editingLocked || this.saving) return;
    const confirmed = await this.confirmation.confirmAction(
      `Remove “${page.name || 'Untitled page'}” and its product placements?`, 'Remove page');
    if (!confirmed) return;
    const removedIndex = this.pages.indexOf(page);
    const previousActiveId = this.activePageId;
    this.pages = this.pages.filter(item => item !== page);
    const selectedIndex = Math.min(Math.max(0, removedIndex), this.pages.length - 1);
    this.activePageId = this.pages.some(item => item.id === previousActiveId)
      ? previousActiveId
      : this.pages[selectedIndex]?.id ?? null;
    this.pageSearchQuery = '';
    this.limitError = '';
    this.refresh();
    this.jumpPageNumber = this.activePageIndex + 1;
    this.revealActivePage();
    this.markDirty();
  }

  renamePage(page: CataloguePage, name: string): void {
    if (this.editingLocked || this.saving) return;
    page.name = name ?? '';
    this.refresh();
    this.markDirty();
  }

  /** Applies a capacity edit if valid; otherwise explains why and puts the previous value back. */
  updateCapacity(page: CataloguePage, value: string | number | null, editor?: ResettableEditor): void {
    const restore = () => editor?.option('value', page.capacity);
    if (this.editingLocked || this.saving) { restore(); return; }
    const capacity = Number(value);
    if (value === null || value === '' || !Number.isInteger(capacity) || capacity < 1 || capacity > CATALOGUE_LIMITS.maxCapacity) {
      this.capacityError = `Capacity must be a whole number from 1 to ${CATALOGUE_LIMITS.maxCapacity.toLocaleString('en-US')}.`;
      restore();
      return;
    }
    if (capacity < page.products.length) {
      this.capacityError = `Capacity cannot be below ${page.products.length} assigned products. Remove placements first or increase capacity.`;
      restore();
      return;
    }
    page.capacity = capacity;
    this.capacityError = '';
    this.refresh();
    this.markDirty();
  }

  movePage(index: number, offset: number): void {
    if (this.editingLocked || this.saving) return;
    const target = index + offset;
    if (target < 0 || target >= this.pages.length) return;
    const reordered = [...this.pages];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    this.pages = reordered;
    this.refresh();
    this.jumpPageNumber = this.activePageIndex + 1;
    this.revealActivePage();
    this.markDirty();
  }

  /** Opens the shared product-selection grid with the products that can still be placed. */
  openProductSelection(): void {
    if (!this.canAddProducts) return;
    this.productSelection?.loadRows(this.availableProducts.map((product): ProductSelectionRow => ({
      id: `${product.productId}:${product.promotionGroupId}`,
      product_code: product.code,
      product_name: product.name,
      supplier_name: product.supplierName ?? '',
      product_group_name: product.promotionGroupName ?? '',
      category_name: product.category,
      department_description: product.departmentDescription ?? '',
      category_description: product.categoryDescription ?? '',
      host_product_group_description: product.productTypeDescription ?? '',
      product_range: product.productRange ?? ''
    })));
    this.productSelectionVisible = true;
  }

  closeProductSelection(): void {
    this.productSelectionVisible = false;
  }

  /**
   * Places the rows chosen in the product-selection grid on the active page, in selection order.
   * Products already placed (or picked twice under different promotion groups) are skipped;
   * placement stops at the page capacity and the catalogue limits, and says how many fitted.
   */
  addProducts(rows: Array<Pick<ProductSelectionRow, 'id'>>): void {
    const page = this.activePage;
    if (this.editingLocked || this.saving || !page) return;
    const available = new Set(this.availableProducts.map(product => `${product.productId}:${product.promotionGroupId}`));
    const chosen = rows.map(row => String(row.id)).filter(key => available.has(key));
    const taken = new Set(this.assignedIds);
    const added: CataloguePlacement[] = [];
    let stopReason = '';
    for (const key of chosen) {
      const [productId, promotionGroupId] = key.split(':').map(Number);
      if (taken.has(productId)) continue;
      const onPage = page.products.length + added.length;
      if (onPage >= page.capacity) {
        stopReason = 'the page is at capacity';
        break;
      }
      if (onPage >= CATALOGUE_LIMITS.maxProductsPerPage) {
        stopReason = `a page can hold at most ${CATALOGUE_LIMITS.maxProductsPerPage.toLocaleString('en-US')} products`;
        break;
      }
      if (taken.size >= CATALOGUE_LIMITS.maxPlacements) {
        stopReason = `a catalogue can have at most ${CATALOGUE_LIMITS.maxPlacements.toLocaleString('en-US')} product placements`;
        break;
      }
      added.push({ productId, promotionGroupId, position: onPage + 1 });
      taken.add(productId);
    }
    this.limitError = stopReason ? `Placed ${added.length} of ${chosen.length} selected products: ${stopReason}.` : '';
    if (!added.length) return;
    page.products = [...page.products, ...added];
    this.refresh();
    this.markDirty();
  }

  removeProduct(productId: number): void {
    if (this.editingLocked || this.saving) return;
    const page = this.activePage;
    if (!page) return;
    page.products = page.products.filter(product => product.productId !== productId);
    this.limitError = '';
    this.refresh();
    this.markDirty();
  }

  moveProduct(index: number, offset: number): void {
    if (this.editingLocked || this.saving) return;
    const page = this.activePage;
    const target = index + offset;
    if (!page || target < 0 || target >= page.products.length) return;
    const products = [...page.products];
    [products[index], products[target]] = [products[target], products[index]];
    page.products = products;
    this.refresh();
    this.markDirty();
  }

  markDirty(): void {
    if (this.editingLocked || this.saving) return;
    this.dirty = true;
    this.saveError = '';
    this.validationMessages = [];
    this.notice = '';
  }

  async save(): Promise<void> {
    if (this.saving || this.editingLocked || !this.dirty || !this.validate()) return;
    this.saving = true;
    this.saveError = '';
    this.validationMessages = [];
    this.conflict = false;
    const request: CatalogueSaveRequest = {
      expectedVersion: this.version,
      pages: this.pages.map((page, index) => ({
        ...(typeof page.id === 'string' && page.id.startsWith('new-') ? {} : { id: page.id }),
        name: page.name.trim(),
        planningCategoryId: page.planningCategoryId,
        capacity: page.capacity,
        position: index,
        products: page.products.map((product, productIndex) => ({
          productId: product.productId,
          promotionGroupId: product.promotionGroupId,
          position: productIndex
        }))
      }))
    };
    try {
      const saved = await firstValueFrom(this.catalogue.savePlan(this.promotionId, request));
      this.version = Number(saved?.version ?? (this.version + 1));
      // The contract's PUT response contains only promotionHeaderId and version. Re-read to receive
      // server-assigned IDs for new pages; keep the local copy if that follow-up read fails.
      try {
        const persisted = await firstValueFrom(this.catalogue.getPlan(this.promotionId));
        if (Number(persisted?.version || 0) >= this.version && Array.isArray(persisted?.pages)) this.applySavedPlan(persisted);
      } catch { /* Save succeeded; don't turn a refresh failure into a failed save. */ }
      this.dirty = false;
      this.notice = 'Catalogue changes saved.';
    } catch (error: any) {
      const serverMessage = this.serverMessage(error);
      this.conflict = error?.status === 409 && (
        serverMessage.toLowerCase().includes('changed by another planner') || error?.error?.data?.expectedVersion != null
      );
      if (error?.status === 403 || error?.status === 423 || (error?.status === 409 && !this.conflict)) {
        this.editingLocked = true;
        this.lockedReason = serverMessage || this.errorMessage(error, 'This promotion cannot accept catalogue changes.');
      }
      this.validationMessages = [...this.fieldMessages(error), ...this.conflictingProductMessages(error)];
      this.saveError = this.errorMessage(error, 'Changes could not be saved. Your edits are still here.');
    } finally { this.saving = false; }
  }

  validate(): boolean {
    this.validationMessages = [];
    if (this.pages.length > CATALOGUE_LIMITS.maxPages) {
      this.saveError = `A catalogue can have at most ${CATALOGUE_LIMITS.maxPages} pages.`;
      return false;
    }
    if (this.pages.some(page => page.products.length > CATALOGUE_LIMITS.maxProductsPerPage)) {
      this.saveError = `A page can hold at most ${CATALOGUE_LIMITS.maxProductsPerPage.toLocaleString('en-US')} products.`;
      return false;
    }
    const placements = this.pages.reduce((count, page) => count + page.products.length, 0);
    if (placements > CATALOGUE_LIMITS.maxPlacements) {
      this.saveError = `A catalogue can have at most ${CATALOGUE_LIMITS.maxPlacements.toLocaleString('en-US')} product placements.`;
      return false;
    }
    if (this.pages.some(page => page.name.trim().length > CATALOGUE_LIMITS.maxPageNameLength)) {
      this.saveError = `Page names must be ${CATALOGUE_LIMITS.maxPageNameLength} characters or fewer.`;
      return false;
    }
    if (!this.pages.every(page => page.name.trim() &&
      Number.isInteger(page.capacity) && page.capacity > 0 && page.capacity <= CATALOGUE_LIMITS.maxCapacity && page.products.length <= page.capacity)) {
      this.saveError = `Each page needs a name. Capacity must be a whole number from 1 to ${CATALOGUE_LIMITS.maxCapacity.toLocaleString('en-US')} and fit its assigned products.`;
      return false;
    }
    if (this.assignedIds.size !== placements) {
      this.saveError = 'A product can only be placed once in this promotion.';
      return false;
    }
    return true;
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (this.saving) {
      this.notice = '';
      this.saveError = 'Save in progress. Wait for it to finish before leaving this page.';
      return false;
    }
    return this.dirty
      ? this.confirmation.confirmAction('You have unsaved catalogue changes. Leave this page and discard them?', 'Unsaved changes')
      : true;
  }

  backToPromotions(): void {
    if (this.saving) return;
    this.router.navigate(['/promotions']);
  }

  async reloadAfterConflict(): Promise<void> {
    if (!this.dirty || await this.confirmation.confirmAction('Reload the latest saved version? Your unsaved page edits will be discarded.', 'Reload')) {
      await this.load();
    }
  }

  dismissError(): void {
    this.saveError = '';
    this.validationMessages = [];
    this.conflict = false;
  }

  private applyEditability(plan: Pick<CataloguePlan, 'canEdit' | 'readOnlyReason'>): void {
    this.editingLocked = plan.canEdit === false;
    this.lockedReason = plan.readOnlyReason
      || (this.editingLocked ? 'This promotion cannot be changed because it is closed or discount review has started.' : '');
  }

  private toPages(plan: { pages?: CataloguePageDto[] }): CataloguePage[] {
    return (plan?.pages || []).map(page => ({
      id: page.id,
      name: page.name || '',
      planningCategoryId: page.planningCategoryId ?? null,
      category: page.planningCategoryName ?? '',
      capacity: Number(page.capacity),
      position: Number(page.position || 0),
      products: (page.products || []).map((item): CataloguePlacement => ({
        productId: Number(item.productId),
        promotionGroupId: Number(item.promotionGroupId),
        promotionDetailId: item.promotionDetailId || undefined,
        position: Number(item.position ?? 0),
        productCode: item.productCode ?? '',
        productName: item.productName ?? '',
        planningCategoryName: page.planningCategoryName ?? ''
      }))
    }));
  }

  private toProducts(feed: CatalogueProductFeed): CatalogueProduct[] {
    return (feed?.categories || []).flatMap(category => (category.products || []).map((row): CatalogueProduct => ({
      productId: Number(row.productId),
      promotionGroupId: Number(row.promotionGroupId),
      code: String(row.productCode ?? ''),
      name: String(row.productName ?? ''),
      planningCategoryId: Number(category.id),
      category: String(category.name ?? ''),
      supplierName: String(row.supplierName ?? ''),
      promotionGroupName: String(row.promotionGroupName ?? ''),
      departmentDescription: String(row.departmentDescription ?? ''),
      categoryDescription: String(row.categoryDescription ?? ''),
      productTypeDescription: String(row.productTypeDescription ?? ''),
      productRange: String(row.productRange ?? '')
    })));
  }

  private applySavedPlan(plan: CataloguePlan): void {
    this.version = Number(plan.version || 0);
    this.pages = this.toPages(plan);
    this.activePageId = this.pages.some(page => page.id === this.activePageId)
      ? this.activePageId
      : this.pages[0]?.id ?? null;
    if (plan.canEdit !== undefined) {
      this.applyEditability(plan);
      if (!this.editingLocked) this.lockedReason = '';
    }
    this.refresh();
    this.jumpPageNumber = this.activePageIndex + 1;
  }

  private errorMessage(error: any, fallback: string): string {
    const message = this.serverMessage(error);
    if (error?.status === 409) return message || 'This catalogue changed elsewhere. Your edits are preserved; reload to review the latest version before saving again.';
    if (error?.status === 403) return message || 'You do not have permission to edit this promotion catalogue.';
    if (error?.status === 423) return message || 'This promotion is closed or cannot accept catalogue changes.';
    if (error?.status === 400 || error?.status === 422) return message || 'Review the page details and product placements, then try again.';
    if (error?.status === 404) return 'This promotion was not found or is outside your tenant.';
    return message || fallback;
  }

  private serverMessage(error: any): string {
    return typeof error?.error?.message === 'string' ? error.error.message : '';
  }

  /**
   * Turns the API's validation errors ({ "pages.0.name": ["..."] }) into readable lines such as
   * "Page 1 name: The pages.0.name field is required." Returns [] for any other error.
   */
  private fieldMessages(error: any): string[] {
    if (error?.status !== 400 && error?.status !== 422) return [];
    const data = error?.error?.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
    const lines: string[] = [];
    for (const [field, raw] of Object.entries(data)) {
      const values = Array.isArray(raw) ? raw : [raw];
      if (field === 'productId') {
        // The API names the rejected product by id; show the product instead.
        for (const id of values) lines.push(`Product: ${this.productLabel(Number(id))}`);
        continue;
      }
      const messages = values.filter(item => typeof item === 'string') as string[];
      const label = this.fieldLabel(field);
      for (const message of messages) lines.push(label ? `${label}: ${message}` : message);
    }
    return lines.slice(0, 10);
  }

  /** Lists the products whose non-catalogue detail lines block the save (409). */
  private conflictingProductMessages(error: any): string[] {
    const ids = error?.status === 409 ? error?.error?.data?.conflictingProductIds : null;
    if (!Array.isArray(ids)) return [];
    const lines = ids.slice(0, 10).map((id: unknown) => `Product: ${this.productLabel(Number(id))}`);
    if (ids.length > 10) lines.push(`…and ${ids.length - 10} more`);
    return lines;
  }

  private productLabel(productId: number): string {
    const placement = this.pages.flatMap(page => page.products).find(item => item.productId === productId);
    const product = this.products.find(item => item.productId === productId);
    const name = product?.name || placement?.productName;
    const code = product?.code || placement?.productCode;
    return name ? `${name}${code ? ` · ${code}` : ''}` : `product ${productId}`;
  }

  private fieldLabel(field: string): string {
    const match = /^pages\.(\d+)(?:\.(\w+))?(?:\.(\d+)(?:\.(\w+))?)?$/.exec(field);
    if (!match) return field === 'pages' ? 'Pages' : field === 'expectedVersion' ? 'Version' : '';
    const page = `Page ${Number(match[1]) + 1}`;
    if (!match[2]) return page;
    if (match[2] === 'products' && match[3] !== undefined) return `${page}, product ${Number(match[3]) + 1}`;
    return `${page} ${match[2] === 'planningCategoryId' ? 'category' : match[2]}`;
  }
}
